import { Container, Grid, Button, CircularProgress } from "@mui/material";
import { Dispatch, RefObject } from "react";
import { WorkoutFormModel } from "../../lib/workout-form/transformers";
import { Action, FormInputs } from "../../reducers/workoutReducer";
import BodyInput from "./inputs/body-input";

import CollectionTimeInput from "./inputs/collectionTime-input";
import DateInput from "./inputs/date-input";
import GearInput from "./inputs/gear-input";
import LocationInput from "./inputs/location-input";
import NumberOfPlacesInput from "./inputs/numberOfPlaces-input";
import RegistrationTimeInput from "./inputs/registrationTime-Input";
import TimeInput from "./inputs/time-input";
import TitleInput from "./inputs/title-input";
import CoachInput from "./inputs/coaches-input";
import AppHeader from "../app-header";
import { useParams } from "react-router-dom";
import { useWorkouts } from "../../providers/workouts";
import CreditsInput from "./inputs/credits-input";
import RecurrenceInput from "./inputs/recurrence-input";
import { features } from "../../core/config/features";

type Props = {
  dispatch: Dispatch<Action>;
  onSubmit: () => void;
  onDelete: () => void;
  workout: WorkoutFormModel;
  edit: boolean;
  loading: boolean;
  handleErrorRefs: RefObject<(() => void)[]>;
  recurrenceCount: number;
  setRecurrenceCount: (count: number) => void;
};

const ViewWorkout = ({
  handleErrorRefs,
  onDelete,
  dispatch,
  onSubmit,
  workout,
  edit,
  loading,
  recurrenceCount,
  setRecurrenceCount,
}: Props) => {
  const { workouts } = useWorkouts();
  const { id } = useParams();
  const [w] = workouts.filter((w) => w.id === id).map((w, key) => w);

  return (
    <Container maxWidth="xs" sx={{ position: "relative"}}>
      <AppHeader edit={edit} workout={w} onDelete={onDelete} />
      <Grid
        container
        spacing={2}
        flexDirection={"column"}
        sx={{ paddingTop: "6rem" }}>
        <Grid item>
          <TitleInput
            handleErrorRefs={handleErrorRefs}
            type={workout[FormInputs.type]}
            dispatch={dispatch}
            loading={loading}
          />
        </Grid>
        <Grid container item spacing={2}>
          <Grid item xs={features.credits ? 3 : 4}>
            <NumberOfPlacesInput
              handleErrorRefs={handleErrorRefs}
              dispatch={dispatch}
              loading={loading}
              numberOfPlaces={workout[FormInputs.numberOfPlaces]}
            />
          </Grid>
          {features.credits && (
            <Grid item xs={3}>
              <CreditsInput
                handleErrorRefs={handleErrorRefs}
                dispatch={dispatch}
                loading={loading}
                credits={workout[FormInputs.credits]}
              />
            </Grid>
          )}
          <Grid item xs={features.credits ? 6 : 8}>
            <DateInput
              handleErrorRefs={handleErrorRefs}
              date={workout[FormInputs.date]}
              dispatch={dispatch}
              loading={loading}
            />
          </Grid>
          <Grid item xs={6}>
            <TimeInput
              handleErrorRefs={handleErrorRefs}
              type={FormInputs.startTime}
              dispatch={dispatch}
              loading={loading}
              startTime={workout[FormInputs.startTime]}
              endTime={workout[FormInputs.endTime]}
            />
          </Grid>
          <Grid item xs={6}>
            <TimeInput
              handleErrorRefs={handleErrorRefs}
              type={FormInputs.endTime}
              dispatch={dispatch}
              loading={loading}
              startTime={workout[FormInputs.startTime]}
              endTime={workout[FormInputs.endTime]}
            />
          </Grid>
          <Grid item xs={6}>
            <CollectionTimeInput
              handleErrorRefs={handleErrorRefs}
              dispatch={dispatch}
              loading={loading}
              collectionTime={workout[FormInputs.collectionTime]}
            />
          </Grid>
          <Grid item xs={6}>
            <RegistrationTimeInput
              handleErrorRefs={handleErrorRefs}
              dispatch={dispatch}
              loading={loading}
              registrationTime={workout[FormInputs.registrationTime]}
            />
          </Grid>
        </Grid>
        <Grid item>
          <LocationInput
            handleErrorRefs={handleErrorRefs}
            dispatch={dispatch}
            loading={loading}
            location={workout[FormInputs.location]}
          />
        </Grid>
        <Grid item>
          <RecurrenceInput
            disabled={loading}
            edit={edit}
            recurrenceCount={recurrenceCount}
            setRecurrenceCount={setRecurrenceCount}
          />
        </Grid>
        <Grid item>
          <GearInput
            handleErrorRefs={handleErrorRefs}
            dispatch={dispatch}
            loading={loading}
            gear={workout[FormInputs.gear]}
          />
        </Grid>
        {
          <Grid item>
            <CoachInput
              dispatch={dispatch}
              loading={loading}
              coaches={workout[FormInputs.coaches]}
            />
          </Grid>
        }
        <Grid item>
          <BodyInput
            handleErrorRefs={handleErrorRefs}
            body={workout[FormInputs.body]}
            dispatch={dispatch}
            loading={loading}
          />
        </Grid>
        <Grid item container justifyContent={"space-between"} sx={{paddingBottom: "10rem"}}>
          <Grid item>
            <Button
              disabled={loading}
              variant="outlined"
              color="error"
              onClick={(e) =>
                dispatch({ type: FormInputs.clear, payload: null })
              }>
              Återställ
            </Button>
          </Grid>
          <Grid item>
            <Button
              disabled={loading}
              variant="outlined"
              onClick={() => onSubmit()}>
              {loading ? <CircularProgress /> : "Spara"}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ViewWorkout;
